// Shared helper for mutation routes that need a stable Idempotency-Key
// derived from the request payload. Both `createPaymentPage` and
// `acceptAragCase` use this pattern: hash the canonical payload so a
// retry of the same logical operation coalesces against the same durable
// idempotency reservation on the server side.
//
// Hashing (rather than a `<prefix>-<field1>-<field2>` concat) avoids the
// hyphen-collision class of bug Cursor Bugbot flagged on PR #343 — fields
// that contain hyphens (e.g. ARAG case-assist numbers like `CA-12345`)
// could collapse two distinct `(field1, field2)` pairs to the same key.

export const buildDeterministicIdempotencyKey = (props: {
  prefix: string;
  requestData: unknown;
}): string => {
  const canonicalRequest = JSON.stringify(normalizeValue(props.requestData));
  let hashA = 5381;
  let hashB = 52711;

  for (let index = 0; index < canonicalRequest.length; index += 1) {
    const codePoint = canonicalRequest.charCodeAt(index);
    hashA = Math.imul(hashA, 33) ^ codePoint;
    hashB = Math.imul(hashB, 33) ^ codePoint;
  }

  return `${props.prefix}-${(hashA >>> 0).toString(36)}${(hashB >>> 0).toString(36)}`;
};

// Sort object keys before stringifying so two payloads that differ only
// in key order produce the same hash. Recursive to handle nested objects
// and arrays of objects.
const normalizeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeValue(entry));
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => {
          if (left < right) {
            return -1;
          }

          if (left > right) {
            return 1;
          }

          return 0;
        })
        .map(([key, entryValue]) => [key, normalizeValue(entryValue)]),
    );
  }

  return value;
};
